import { useState, useEffect } from "react";
import {
  Search,
  Filter,
  Users,
  Mail,
  Phone,
  Edit,
  Trash2,
  Eye,
  Plus,
  MoreVertical,
  Download,
  X,
  AlertCircle,
} from "lucide-react";

import { getAllUser, getUserById } from "../../../services/api";
import { Link } from "react-router-dom";

const AllUsers = () => {
  // Store all users and filtered users separately
  const [allUsersData, setAllUsersData] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    role: "",
    status: "",
    joinedDate: "",
    location: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [page, setPage] = useState(1);
  const [actionLoading, setActionLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Pagination settings
  const ITEMS_PER_PAGE = 20;

  // Debounce search to prevent too many filter operations
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Initial data fetch
  useEffect(() => {
    if (isInitialLoad) {
      fetchAllUsers();
    }
  }, []);

  // Apply filters when search term or filters change
  useEffect(() => {
    if (!isInitialLoad) {
      applyFilters();
    }
  }, [debouncedSearchTerm, filters]);

  // Reset page when filters change
  useEffect(() => {
    if (!isInitialLoad) {
      setPage(1);
    }
  }, [debouncedSearchTerm, filters]);

  // Fetch all users from API (only once)
  const fetchAllUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all users at once
      const response = await getAllUser({ limit: 1000 });

      // Handle the response structure
      let usersData = [];
      if (response.success && response.data) {
        usersData = response.data;
      } else {
        // Fallback for different response structures
        usersData = response.users || response.data || response;
        usersData = Array.isArray(usersData) ? usersData : [];
      }

      console.log("Fetched users:", usersData.length);
      setAllUsersData(usersData);
      setFilteredUsers(usersData);
      setIsInitialLoad(false);
    } catch (err) {
      setError(err.message || "Failed to fetch users");
      setAllUsersData([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering function
  const applyFilters = () => {
    console.log("Applying filters:", {
      filters,
      searchTerm: debouncedSearchTerm,
    });

    let filtered = [...allUsersData];

    // Apply search filter
    if (debouncedSearchTerm.trim()) {
      const searchLower = debouncedSearchTerm.toLowerCase().trim();
      filtered = filtered.filter((user) => {
        const name =
          user.name ||
          (user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : user.firstName) ||
          "";
        const email = user.email || "";
        const phone = user.phone || "";
        const role = user.role || "";

        return (
          name.toLowerCase().includes(searchLower) ||
          email.toLowerCase().includes(searchLower) ||
          phone.toLowerCase().includes(searchLower) ||
          role.toLowerCase().includes(searchLower)
        );
      });
    }

    // Apply role filter
    if (filters.role) {
      filtered = filtered.filter(
        (user) => user.role?.toLowerCase() === filters.role.toLowerCase()
      );
    }

    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter((user) => {
        const userStatus = user.status || "active"; // Default to active if no status
        return userStatus.toLowerCase() === filters.status.toLowerCase();
      });
    }

    // Apply joined date filter
    if (filters.joinedDate) {
      filtered = filtered.filter((user) => {
        const userDate = user.createdAt || user.joinedDate;
        if (!userDate) return false;

        const userDateOnly = new Date(userDate).toISOString().split("T")[0];
        return userDateOnly === filters.joinedDate;
      });
    }

    // Apply location filter
    if (filters.location.trim()) {
      const locationLower = filters.location.toLowerCase().trim();
      filtered = filtered.filter((user) => {
        const address = user.address || user.location || "";
        return address.toLowerCase().includes(locationLower);
      });
    }

    console.log(
      "Filtered users:",
      filtered.length,
      "out of",
      allUsersData.length
    );
    setFilteredUsers(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    console.log(`Changing filter ${filterType} to:`, value);
    setFilters((prev) => {
      const newFilters = {
        ...prev,
        [filterType]: value,
      };
      console.log("New filters state:", newFilters);
      return newFilters;
    });
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearFilters = () => {
    console.log("Clearing all filters");
    setFilters({
      role: "",
      status: "",
      joinedDate: "",
      location: "",
    });
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setPage(1);
  };

  const handleViewUser = async (userId) => {
    try {
      setActionLoading(true);
      const response = await getUserById(userId);
      setSelectedUser(response.success ? response.data : response);
      setShowUserModal(true);
    } catch (err) {
      setError(err.message || "Failed to fetch user details");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchAllUsers();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case "admin":
        return "bg-red-100 text-red-800";
      case "doctor":
      case "veterinarian":
        return "bg-blue-100 text-blue-800";
      case "client":
        return "bg-green-100 text-green-800";
      case "user":
      case "pet_owner":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-purple-100 text-purple-800";
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-800";
      case "inactive":
      case "suspended":
        return "bg-red-100 text-red-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Check if any filters are active
  const hasActiveFilters =
    searchTerm ||
    filters.role ||
    filters.status ||
    filters.joinedDate ||
    filters.location;

  // Get current page users for pagination
  const getCurrentPageUsers = () => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredUsers.slice(startIndex, endIndex);
  };

  // Calculate total pages for client-side pagination
  const totalPagesCalculated = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);

  // Get current page users
  const currentPageUsers = getCurrentPageUsers();

  // Role options - predefined list
  const roleOptions = ["admin", "doctor", "client", "user", "pet_owner"];
  const statusOptions = ["active", "inactive", "pending", "suspended"];

  if (loading && isInitialLoad) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">All Users</h1>
              <p className="text-gray-600 mt-1">
                {filteredUsers.length} of {allUsersData.length}{" "}
                {filteredUsers.length === 1 ? "user" : "users"} found
                {hasActiveFilters && (
                  <span className="text-blue-600 ml-2">• Filters applied</span>
                )}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              {/* <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2">
                <Download size={20} />
                Export
              </button> */}
              <Link to={"/dashboard/add-doctor"}>
                <button className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
                  <Plus className="w-4 h-4" />
                  <span>Add Doctor</span>
                </button>
              </Link>
              <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
                <Plus size={20} />
                Add User
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg mb-6 flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              {error}
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-600 hover:text-red-800"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="relative flex-1">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Search users by name, email, or phone..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                showFilters || hasActiveFilters
                  ? "bg-blue-100 text-blue-700 border border-blue-200"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              <Filter size={20} />
              Filters
              {hasActiveFilters && !showFilters && (
                <span className="bg-blue-600 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                  {
                    [
                      searchTerm,
                      filters.role,
                      filters.status,
                      filters.joinedDate,
                      filters.location,
                    ].filter(Boolean).length
                  }
                </span>
              )}
            </button>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="bg-red-100 text-red-700 px-4 py-2 rounded-lg hover:bg-red-200 transition-colors flex items-center gap-2"
              >
                <X size={20} />
                Clear All
              </button>
            )}
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="bg-blue-100 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-200 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <svg
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh
            </button>
          </div>

          {/* Active Filters Display */}
          {hasActiveFilters && !showFilters && (
            <div className="flex flex-wrap gap-2 mb-4">
              {searchTerm && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                  Search: "{searchTerm}"
                  <button
                    onClick={() => setSearchTerm("")}
                    className="hover:text-blue-600"
                  >
                    <X size={14} />
                  </button>
                </span>
              )}
              {filters.role && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                  Role: {filters.role}
                  <button
                    onClick={() => handleFilterChange("role", "")}
                    className="hover:text-green-600"
                  >
                    <X size={14} />
                  </button>
                </span>
              )}
              {filters.status && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">
                  Status: {filters.status}
                  <button
                    onClick={() => handleFilterChange("status", "")}
                    className="hover:text-purple-600"
                  >
                    <X size={14} />
                  </button>
                </span>
              )}
              {filters.joinedDate && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-800 text-sm rounded-full">
                  Date: {filters.joinedDate}
                  <button
                    onClick={() => handleFilterChange("joinedDate", "")}
                    className="hover:text-orange-600"
                  >
                    <X size={14} />
                  </button>
                </span>
              )}
              {filters.location && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-pink-100 text-pink-800 text-sm rounded-full">
                  Location: {filters.location}
                  <button
                    onClick={() => handleFilterChange("location", "")}
                    className="hover:text-pink-600"
                  >
                    <X size={14} />
                  </button>
                </span>
              )}
            </div>
          )}

          {/* Extended Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg border">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role
                </label>
                <select
                  value={filters.role}
                  onChange={(e) => {
                    console.log("Role dropdown changed to:", e.target.value);
                    handleFilterChange("role", e.target.value);
                  }}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  <option value="">All Roles</option>
                  {roleOptions.map((role) => (
                    <option key={role} value={role}>
                      {role === "pet_owner"
                        ? "Pet Owner"
                        : role.charAt(0).toUpperCase() + role.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => {
                    console.log("Status dropdown changed to:", e.target.value);
                    handleFilterChange("status", e.target.value);
                  }}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                >
                  <option value="">All Statuses</option>
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Joined Date
                </label>
                <input
                  type="date"
                  value={filters.joinedDate}
                  onChange={(e) => {
                    console.log("Joined date changed to:", e.target.value);
                    handleFilterChange("joinedDate", e.target.value);
                  }}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  placeholder="Enter location"
                  value={filters.location}
                  onChange={(e) => {
                    console.log("Location changed to:", e.target.value);
                    handleFilterChange("location", e.target.value);
                  }}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              <div className="sm:col-span-2 lg:col-span-4 flex justify-between items-center">
                <button
                  onClick={clearFilters}
                  className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center gap-1"
                >
                  <X size={16} />
                  Clear all filters
                </button>
                <button
                  onClick={() => setShowFilters(false)}
                  className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                >
                  Hide Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Subtle loading indicator for filter changes */}
        {loading && !isInitialLoad && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            <span className="text-blue-700 text-sm">Loading users...</span>
          </div>
        )}

        {/* Users Table */}
        {filteredUsers.length === 0 && !loading && !isInitialLoad ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {hasActiveFilters
                ? "No users match your criteria"
                : "No users found"}
            </h3>
            <p className="text-gray-600 mb-4">
              {hasActiveFilters
                ? "Try adjusting your search criteria or filters."
                : "Try adding a new user to get started."}
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Clear all filters
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentPageUsers.map((user) => (
                    <tr key={user._id || user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {user.avatar ? (
                              <img
                                className="h-10 w-10 rounded-full object-cover"
                                src={user.avatar}
                                alt={user.name || user.firstName || "User"}
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <span className="text-blue-600 font-medium text-sm">
                                  {getInitials(
                                    user.name || user.firstName || user.email
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {console.log("User structure:", {
                                id: user._id || user.id,
                                name: user.name,
                                firstName: user.firstName,
                                lastName: user.lastName,
                                email: user.email,
                                role: user.role,
                                status: user.status,
                              })}
                              {user.role == "doctor"
                                ? user.doctorDetails.name
                                : user.userDetails.name || "Unnamed User"}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.email || "No email"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center mb-1">
                            <Mail className="w-4 h-4 text-gray-400 mr-2" />
                            {user.userDetails?.contact?.mobile ||
                              user.doctorDetails?.contact?.mobile ||
                              "N/A"}
                          </div>
                          {user.phone && (
                            <div className="flex items-center">
                              <Phone className="w-4 h-4 text-gray-400 mr-2" />
                              {user.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(
                            user.role
                          )}`}
                        >
                          {user.role
                            ? user.role === "pet_owner"
                              ? "Pet Owner"
                              : user.role.charAt(0).toUpperCase() +
                                user.role.slice(1)
                            : "User"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                            user.status
                          )}`}
                        >
                          {user.status
                            ? user.status.charAt(0).toUpperCase() +
                              user.status.slice(1)
                            : "Active"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(user.createdAt || user.joinedDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewUser(user._id || user.id)}
                            disabled={actionLoading}
                            className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                            title="View Details"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            className="text-green-600 hover:text-green-900"
                            title="Edit User"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            className="text-red-600 hover:text-red-900"
                            title="Delete User"
                          >
                            <Trash2 size={18} />
                          </button>
                          {/* <button
                            className="text-gray-600 hover:text-gray-900"
                            title="More Options"
                          >
                            <MoreVertical size={18} />
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPagesCalculated > 1 && (
          <div className="flex justify-center mt-8">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                Previous
              </button>

              {/* Show page numbers with ellipsis for large page counts */}
              {Array.from(
                { length: Math.min(totalPagesCalculated, 5) },
                (_, i) => {
                  let pageNum;
                  if (totalPagesCalculated <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPagesCalculated - 2) {
                    pageNum = totalPagesCalculated - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`px-4 py-2 border rounded-lg transition-colors ${
                        page === pageNum
                          ? "bg-blue-600 text-white border-blue-600"
                          : "border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                }
              )}

              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPagesCalculated}
                className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* User Details Modal */}
        {showUserModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">
                    User Details
                  </h2>
                  <button
                    onClick={() => setShowUserModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X size={24} />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="text-center md:text-left">
                    {selectedUser.avatar ? (
                      <img
                        className="h-24 w-24 rounded-full mx-auto md:mx-0 object-cover"
                        src={selectedUser.avatar}
                        alt={selectedUser.name || "User"}
                      />
                    ) : (
                      <div className="h-24 w-24 rounded-full bg-blue-100 flex items-center justify-center mx-auto md:mx-0">
                        <span className="text-blue-600 font-bold text-2xl">
                          {getInitials(
                            selectedUser.name ||
                              selectedUser.firstName ||
                              selectedUser.email
                          )}
                        </span>
                      </div>
                    )}
                    <h3 className="mt-4 text-xl font-semibold text-gray-900">
                      {console.log(selectedUser)}
                      {selectedUser.userDetails.name ||
                        (selectedUser.firstName && selectedUser.lastName
                          ? `${selectedUser.firstName} ${selectedUser.lastName}`
                          : selectedUser.firstName) ||
                        "Unnamed User"}
                    </h3>
                    <p className="text-gray-600">{selectedUser.email}</p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Role
                      </label>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(
                          selectedUser.role
                        )}`}
                      >
                        {selectedUser.role
                          ? selectedUser.role === "pet_owner"
                            ? "Pet Owner"
                            : selectedUser.role.charAt(0).toUpperCase() +
                              selectedUser.role.slice(1)
                          : "User"}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Status
                      </label>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                          selectedUser.status
                        )}`}
                      >
                        {selectedUser.status
                          ? selectedUser.status.charAt(0).toUpperCase() +
                            selectedUser.status.slice(1)
                          : "Active"}
                      </span>
                    </div>
                    {selectedUser.phone && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Phone
                        </label>
                        <p className="text-gray-900">{selectedUser.phone}</p>
                      </div>
                    )}
                    {selectedUser.address && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Address
                        </label>
                        <p className="text-gray-900">{selectedUser.address}</p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Member Since
                      </label>
                      <p className="text-gray-900">
                        {formatDate(
                          selectedUser.createdAt || selectedUser.joinedDate
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setShowUserModal(false)}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      Close
                    </button>
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                      Edit User
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AllUsers;
